/**
 * Zustand store for Featured Device config
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const FEATURED_CONFIG_KEY = "khays_featured_config";
const FEATURED_CACHE_KEY = "khays_featured_cache";

export interface FeaturedConfig {
  enabled: boolean;
  accessoryWeight: number;
  enabledCategories: string[];
  excludedDevices: string[];
  rotationIntervalMinutes: number;
  showPrice: boolean;
  showAvailability: boolean;
  showMedia: boolean;
  title: string;
}

function getDefaultConfig(): FeaturedConfig {
  return {
    enabled: false, accessoryWeight: 60, enabledCategories: [], excludedDevices: [],
    rotationIntervalMinutes: 0, showPrice: true, showAvailability: true, showMedia: true, title: "Featured Pick",
  };
}

function loadFromStorage(): FeaturedConfig {
  try { const s = localStorage.getItem(FEATURED_CONFIG_KEY); if (s) return { ...getDefaultConfig(), ...JSON.parse(s) }; } catch {}
  return getDefaultConfig();
}

interface FeaturedState {
  config: FeaturedConfig;
  _rev: number;
  setConfig: (config: FeaturedConfig) => void;
  reload: () => void;
}

export const useFeaturedStore = create<FeaturedState>()(
  devtools(
    (set, get) => ({
      config: loadFromStorage(),
      _rev: 0,
      setConfig: (config) => {
        localStorage.setItem(FEATURED_CONFIG_KEY, JSON.stringify(config));
        set({ config, _rev: get()._rev + 1 });
      },
      reload: () => set({ config: loadFromStorage(), _rev: get()._rev + 1 }),
    }),
    { name: "FeaturedStore" }
  )
);

export function loadFeaturedConfig(): FeaturedConfig { return useFeaturedStore.getState().config; }
export function saveFeaturedConfig(config: FeaturedConfig): void { useFeaturedStore.getState().setConfig(config); }

interface FeaturedCache { deviceName: string; variantLabel: string; timestamp: number; }

export function getCachedFeatured(): FeaturedCache | null {
  try { const s = localStorage.getItem(FEATURED_CACHE_KEY); if (s) return JSON.parse(s); } catch {} return null;
}

export function setCachedFeatured(deviceName: string, variantLabel: string): void {
  localStorage.setItem(FEATURED_CACHE_KEY, JSON.stringify({ deviceName, variantLabel, timestamp: Date.now() }));
}

export function shouldRotate(config: FeaturedConfig): boolean {
  if (config.rotationIntervalMinutes <= 0) return true;
  const cache = getCachedFeatured();
  if (!cache) return true;
  return (Date.now() - cache.timestamp) / 60000 >= config.rotationIntervalMinutes;
}
